import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Save, Loader2, CheckSquare, Square, Wand2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import {
  useGetUtilisateursQuery,
  useGetPermissionsQuery,
  useCreerPermissionMutation,
  useModifierPermissionMutation,
  useSupprimerPermissionMutation,
  useAppliquerProfilPermissionsMutation,
} from '../../services/coreApi';
import type { PermissionItem } from '../../services/coreApi';

const MODULES = [
  { key: 'etudiants', label: 'Étudiants' },
  { key: 'matieres', label: 'Matières & UE' },
  { key: 'saisie_devoirs', label: 'Saisie Devoirs' },
  { key: 'saisie_examens', label: 'Saisie Examens' },
  { key: 'copies', label: 'Copies & Codes-barres' },
  { key: 'ocr', label: 'OCR' },
  { key: 'rachat', label: 'Rachat' },
  { key: 'releves', label: 'Relevés de Notes' },
  { key: 'resultats', label: 'Résultats & Calculs' },
  { key: 'parametres', label: 'Paramètres système' },
  { key: 'utilisateurs', label: 'Utilisateurs' },
  { key: 'reclamations', label: 'Réclamations' },
  // Portail étudiant (2026-10-05) : réservé aux admins sauf droit donné ici —
  // Lire = consulter les verrous, Modifier = les basculer
  { key: 'verrous', label: 'Centre de verrous (portail)' },
];

const PERM_KEYS = ['peut_lire', 'peut_ecrire', 'peut_modifier', 'peut_supprimer', 'peut_exporter'] as const;
const PERM_LABELS: Record<string, string> = {
  peut_lire: 'Lire',
  peut_ecrire: 'Écrire',
  peut_modifier: 'Modifier',
  peut_supprimer: 'Supprimer',
  peut_exporter: 'Exporter',
};

type PermRow = { [K in typeof PERM_KEYS[number]]: boolean } & { id?: number };
type PermMatrix = Record<string, PermRow>;

const emptyRow = (): PermRow => ({
  peut_lire: false, peut_ecrire: false, peut_modifier: false, peut_supprimer: false, peut_exporter: false,
});

const PermissionsTab: React.FC = () => {
  const { data: utilisateurs, isLoading: loadingUsers } = useGetUtilisateursQuery();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [appliquerProfil, { isLoading: applyingProfile }] = useAppliquerProfilPermissionsMutation();

  const { data: permissions, isLoading: loadingPerms, refetch: refetchPerms } = useGetPermissionsQuery(
    selectedUserId ? { utilisateur: selectedUserId } : undefined,
    { skip: !selectedUserId }
  );

  const [creer] = useCreerPermissionMutation();
  const [modifier] = useModifierPermissionMutation();
  const [supprimer] = useSupprimerPermissionMutation();

  const [matrix, setMatrix] = useState<PermMatrix>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // Build matrix from API permissions
  useEffect(() => {
    const m: PermMatrix = {};
    for (const mod of MODULES) {
      const existing = permissions?.find((p: PermissionItem) => p.module === mod.key);
      if (existing) {
        m[mod.key] = {
          id: existing.id,
          peut_lire: existing.peut_lire,
          peut_ecrire: existing.peut_ecrire,
          peut_modifier: existing.peut_modifier,
          peut_supprimer: existing.peut_supprimer,
          peut_exporter: existing.peut_exporter,
        };
      } else {
        m[mod.key] = emptyRow();
      }
    }
    setMatrix(m);
  }, [permissions]);

  const togglePerm = (module: string, perm: typeof PERM_KEYS[number]) => {
    setMatrix(prev => ({
      ...prev,
      [module]: { ...prev[module], [perm]: !prev[module]?.[perm] },
    }));
  };

  const toggleAllModule = (module: string) => {
    const row = matrix[module];
    if (!row) return;
    const allChecked = PERM_KEYS.every(k => row[k]);
    const newVal = !allChecked;
    setMatrix(prev => ({
      ...prev,
      [module]: { ...prev[module], peut_lire: newVal, peut_ecrire: newVal, peut_modifier: newVal, peut_supprimer: newVal, peut_exporter: newVal },
    }));
  };

  const handleSave = useCallback(async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const promises = MODULES.map(async (mod) => {
        const row = matrix[mod.key];
        if (!row) return;
        const hasAny = PERM_KEYS.some(k => row[k]);
        if (row.id) {
          if (hasAny) {
            // Update existing
            await modifier({ id: row.id, data: { peut_lire: row.peut_lire, peut_ecrire: row.peut_ecrire, peut_modifier: row.peut_modifier, peut_supprimer: row.peut_supprimer, peut_exporter: row.peut_exporter } }).unwrap();
          } else {
            // All unchecked → delete the permission row
            await supprimer(row.id).unwrap();
          }
        } else if (hasAny) {
          // Create new
          await creer({ utilisateur: selectedUserId, module: mod.key, ...row }).unwrap();
        }
      });
      await Promise.allSettled(promises);
      showMsg('success', 'Permissions enregistrées');
    } catch {
      showMsg('error', 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  }, [selectedUserId, matrix, creer, modifier, supprimer]);

  if (loadingUsers) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
            : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield size={20} className="text-blue-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Matrice de permissions</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Définir les droits par module pour chaque utilisateur</p>
          </div>
        </div>
        {selectedUserId && (
          <Button variant="primary" icon={saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        )}
      </div>

      {/* User selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sélectionner un utilisateur</label>
        <select
          className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          value={selectedUserId ?? ''}
          onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
        >
          <option value="">-- Choisir un utilisateur --</option>
          {utilisateurs?.map(u => (
            <option key={u.id} value={u.id}>{u.nom} {u.prenoms} ({u.username}) — {u.role}</option>
          ))}
        </select>
      </div>

      {/* Profils prédéfinis */}
      {selectedUserId && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Wand2 size={16} className="text-purple-500" />
            Appliquer un profil prédéfini (remplace toutes les permissions)
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { profil: 'super_admin', label: 'Super Admin', desc: 'Accès total', color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300' },
              { profil: 'agent_scolarite', label: 'Agent Scolarité', desc: 'Saisie, consultation, export', color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300' },
              { profil: 'consultation', label: 'Consultation', desc: 'Lecture et export uniquement', color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300' },
            ].map(p => (
              <button
                key={p.profil}
                disabled={applyingProfile}
                onClick={async () => {
                  if (!window.confirm(`Appliquer le profil "${p.label}" ? Cela remplacera toutes les permissions actuelles.`)) return;
                  try {
                    const res = await appliquerProfil({ utilisateur_id: selectedUserId, profil: p.profil }).unwrap();
                    setMessage({ type: 'success', text: res.message });
                    refetchPerms();
                  } catch (err: any) {
                    setMessage({ type: 'error', text: err.data?.error || 'Erreur' });
                  }
                }}
                className={`px-4 py-2 text-sm font-medium border rounded-lg transition ${p.color}`}
              >
                <div className="font-semibold">{p.label}</div>
                <div className="text-[10px] opacity-70">{p.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Permission matrix */}
      {selectedUserId && (
        loadingPerms ? (
          <div className="flex justify-center py-8"><Spinner size="md" /></div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-56">Module</th>
                    {PERM_KEYS.map(k => (
                      <th key={k} className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{PERM_LABELS[k]}</th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {MODULES.map((mod) => {
                    const row = matrix[mod.key] || emptyRow();
                    const allChecked = PERM_KEYS.every(k => row[k]);
                    return (
                      <tr key={mod.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{mod.label}</td>
                        {PERM_KEYS.map(k => (
                          <td key={k} className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={row[k]}
                              onChange={() => togglePerm(mod.key, k)}
                              className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                            />
                          </td>
                        ))}
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => toggleAllModule(mod.key)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                            {allChecked
                              ? <CheckSquare size={16} className="text-blue-600 dark:text-blue-400" />
                              : <Square size={16} className="text-gray-400" />
                            }
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {!selectedUserId && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <Shield size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Sélectionnez un utilisateur pour configurer ses permissions</p>
        </div>
      )}
    </div>
  );
};

export default PermissionsTab;
